/**
 * Block 12 PR-B Phase B3 Part C Step 24 — AnketaFormView (Presenter).
 *
 * Orchestrates sections А→Б→В→Г→Д→Е→Ж→И per ADR-0020 §Q3 hardcoded order.
 * Visibility per section controlled by `methodology.defaultSections[]` (server-driven).
 *
 * Pure presenter — все handlers controlled через `actions` (AnketaFormActions
 * from Step 10 useAnketaForm). Container (Step 25) wires actions to dispatch.
 *
 * Reference: ADR-0020 §Q3 (hard-coded order, visibility via methodology), §Q1
 * (template engine), §Q4-§Q5 (override + auto-bind sections).
 */

import type { CalculatorMethodology } from '@pomoshnik/shared';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Header } from '@/components/Header/Header';
import { ANKETA_TYPE_LABELS } from './anketaTypeLabels';
import { DisclaimerBanner } from './components/DisclaimerBanner';
import type { AnketaFormActions } from './useAnketaForm';
import type { AnketaState, MaterialRow } from './types';
import { SectionParams } from './sections/SectionParams';
import { SectionMaterials } from './sections/SectionMaterials';
import { SectionAdditional } from './sections/SectionAdditional';
import { additionalDeeplink } from './additionalMeta';
import { SectionFilling } from './sections/SectionFilling';
import { SectionLighting } from './sections/SectionLighting';
import { SectionConsumables } from './sections/SectionConsumables';
import { SectionServices } from './sections/SectionServices';
import { SectionCustomRows } from './sections/SectionCustomRows';
import { SectionTotal } from './sections/SectionTotal';

/**
 * Hotfix-2B Phase B4 Step 9 — Витрина hint computation.
 *
 * TV-зона conditional warning: когда vitrineOn=true AND hasShelf=true → hint
 * напоминает что фасад шкафа считается только по двум глубинам (audit Bug 16
 * prep). Прочие anketa types — hint undefined.
 *
 * `params.hasShelf` существует только в TvZoneParams refined schema — narrow
 * безопасный через discriminator вместо unsafe cast.
 */
function computeVitrineHint(state: AnketaState): string | undefined {
  if (state.anketaType !== 'tv-zone') return undefined;
  if (!state.additionalToggles['aluminum-glass-vitrine']) return undefined;
  if (state.params.type !== 'tv-zone') return undefined;
  if (!state.params.hasShelf) return undefined;
  return 'Витрина включена — фасад шкафа считается только по двум глубинам.';
}

export interface AnketaFormViewProps {
  state: AnketaState;
  methodology: CalculatorMethodology;
  materialRows: MaterialRow[];
  total: number;
  isValid: boolean;
  validationErrorCount: number;
  actions: AnketaFormActions;
  isSaving: boolean;
  onSave: () => Promise<void> | void;
  onCreateQuote?: () => void;
  /** PR-C1 (ADR-0035) — открыть существующий КП по цепочке. */
  onForward?: () => void;
  /** PR-C1 (ADR-0035) — КП для projectId существует → показать «Вперёд». */
  forwardVisible?: boolean;
  /** Cross-block deeplink handler — Container wires forcePersist + navigate (Step 25). */
  onDeeplink?: (
    rowId: string,
    target: 'decor' | 'partner' | 'furniture' | 'handles',
  ) => Promise<void> | void;
  /** Field-path → error message. Container wraps selectErrorForPath (Step 9). */
  errorForPath: (path: string) => string | undefined;
}

export function AnketaFormView({
  state,
  methodology,
  materialRows,
  total,
  isValid,
  validationErrorCount,
  actions,
  isSaving,
  onSave,
  onCreateQuote,
  onForward,
  forwardVisible,
  onDeeplink,
  errorForPath,
}: AnketaFormViewProps) {
  const sections = new Set(methodology.defaultSections);
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={ANKETA_TYPE_LABELS[state.anketaType]} />
      <div className="flex-1 overflow-y-auto pb-6 pt-3 flex flex-col gap-6">
        <DisclaimerBanner />
        <SectionParams
          fields={methodology.fields}
          params={state.params}
          onParamChange={actions.setParam}
          errorForPath={errorForPath}
        />

        {sections.has('materials') ? (
          <SectionMaterials
            anketaType={state.anketaType}
            params={state.params}
            materialRows={materialRows}
            showCountertop={methodology.showCountertop}
            onParamChange={actions.setParam}
            onMaterialPrice={actions.setMaterialPrice}
            onMaterialQty={actions.overrideMaterialQty}
            onResetMaterialAuto={actions.resetMaterialAuto}
            onDeeplink={onDeeplink}
            errorForPath={errorForPath}
            /* PR-H1 Round 2 (ADR-0026) — per-row brand input. */
            brandSelections={state.brandSelections}
            onBrandChange={actions.setBrand}
          />
        ) : null}

        {sections.has('additional') ? (
          <SectionAdditional
            anketaType={state.anketaType}
            applicableAdditional={methodology.applicableAdditional}
            toggles={state.additionalToggles}
            quantities={state.additionalQuantities}
            prices={state.additionalPrices}
            selections={state.additionalSelections}
            onToggle={actions.toggleAdditional}
            onQty={actions.setAdditionalQty}
            onPrice={actions.setAdditionalPrice}
            onSelection={actions.setAdditionalSelection}
            errorForPath={errorForPath}
            lengths={state.additionalLengths}
            heights={state.additionalHeights}
            pricesPerM2={state.additionalPricesPerM2}
            onLength={actions.setAdditionalLength}
            onHeight={actions.setAdditionalHeight}
            onPricePerM2={actions.setAdditionalPricePerM2}
            vitrineHint={computeVitrineHint(state)}
            /* PR-calc-buttons-unify (ADR-0099) — adapter: per-id target lookup
               (facade-radius/vitrine → partner, остальные → furniture). */
            onDeeplink={onDeeplink ? (id) => onDeeplink(id, additionalDeeplink(id)) : undefined}
            /* PR-H1 Round 3 — per-row brand для Доп.элементов. */
            brandSelections={state.brandSelections}
            onBrand={actions.setBrand}
          />
        ) : null}

        {methodology.showFilling ? (
          <SectionFilling
            toggles={state.fillingToggles}
            quantities={state.fillingQuantities}
            prices={state.fillingPrices}
            onToggle={actions.toggleFilling}
            onQty={actions.setFillingQty}
            onPrice={actions.setFillingPrice}
            onDeeplink={onDeeplink ? (id) => onDeeplink(id, 'furniture') : undefined}
            /* PR-H1 Round 4 — per-row brand для Section Г Filling. */
            brandSelections={state.brandSelections}
            onBrand={actions.setBrand}
          />
        ) : null}

        {sections.has('lighting') ? (
          <SectionLighting
            enabled={state.lightingEnabled}
            quantities={state.lightingQuantities}
            prices={state.lightingPrices}
            onToggleEnabled={actions.toggleLighting}
            onQty={actions.setLightingQty}
            onPrice={actions.setLightingPrice}
            /* PR-calc-buttons-unify (ADR-0099, ADR-0100) — adapter: подсветка → /furniture. */
            onDeeplink={onDeeplink ? (id) => onDeeplink(id, 'furniture') : undefined}
          />
        ) : null}

        {sections.has('consumables') ? (
          <SectionConsumables
            toggles={state.consumableToggles}
            quantities={state.consumableQuantities}
            prices={state.consumablePrices}
            onToggle={actions.toggleConsumable}
            onQty={actions.setConsumableQty}
            onPrice={actions.setConsumablePrice}
            /* PR-calc-buttons-unify (ADR-0099, ADR-0100) — adapter: расходники → /furniture. */
            onDeeplink={onDeeplink ? (id) => onDeeplink(id, 'furniture') : undefined}
          />
        ) : null}

        {sections.has('services') ? (
          <SectionServices
            materialRows={materialRows}
            toggles={state.serviceToggles}
            overrides={state.serviceOverrides}
            prices={state.servicePrices}
            onToggle={actions.toggleService}
            onOverride={actions.overrideServiceQty}
            onResetAuto={actions.resetServiceAuto}
            onPrice={actions.setServicePrice}
            /* PR-calc-buttons-unify (ADR-0099) — adapter: услуги → /partners. */
            onDeeplink={onDeeplink ? (id) => onDeeplink(id, 'partner') : undefined}
          />
        ) : null}

        {/* Hotfix-2B Phase B3 Step 6 — Section «Свои позиции» (Bug 9 closure).
            Render для всех 7 typed anketa types (Other anketa использует
            FreeAnketaFormView с собственным FreeRowsSection). Per прототип
            2.12.2:527 — между Услуги и Total. */}
        <SectionCustomRows
          rows={state.customRows}
          onAdd={actions.addCustomRow}
          onUpdate={actions.updateCustomRow}
          onRemove={actions.removeCustomRow}
        />

        <SectionTotal
          total={total}
          isValid={isValid}
          validationErrorCount={validationErrorCount}
          onSave={onSave}
          onCreateQuote={onCreateQuote}
          isSaving={isSaving}
          onForward={onForward}
          forwardVisible={forwardVisible}
        />
      </div>
      <BottomBar variant="default" />
    </main>
  );
}
